import { useEffect, useId, useRef, useState } from "react";
import { Info, Lock } from "lucide-react";

/** How secondary help copy is shown (D2). */
export type FieldHelpDisplay = "inline" | "tooltip";

export const SENSITIVE_FIELD_HELP =
  "Sent to CardConnect for boarding; only the last 4 digits are kept on file.";

interface FieldHelpProps {
  id?: string;
  text: string;
  display?: FieldHelpDisplay;
  /** `info` for field hints; `lock` for sensitive-field explanation. */
  variant?: "info" | "lock";
}

export function FieldHelp({
  id: idProp,
  text,
  display = "tooltip",
  variant = "info",
}: FieldHelpProps) {
  const autoId = useId();
  const helpId = idProp ?? `field-help-${autoId}`;

  if (display === "inline") {
    return (
      <p id={helpId} className="hint">
        {text}
      </p>
    );
  }

  return (
    <FieldHelpTooltip
      helpId={helpId}
      text={text}
      variant={variant}
    />
  );
}

function FieldHelpTooltip({
  helpId,
  text,
  variant,
}: {
  helpId: string;
  text: string;
  variant: "info" | "lock";
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const panelId = `${helpId}-panel`;
  const Icon = variant === "lock" ? Lock : Info;
  const triggerLabel =
    variant === "lock" ? "Sensitive field information" : "Field help";

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <span
      ref={rootRef}
      className="relative inline-flex align-middle"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        id={`${helpId}-trigger`}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={triggerLabel}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className={`rounded-full p-0.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-bright/40 ${
          variant === "lock"
            ? "text-brand-muted/70 hover:text-brand-slate"
            : "text-brand-muted/70 hover:text-brand-blue-bright"
        }`}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      {open && (
        <span
          id={panelId}
          role="tooltip"
          className="absolute left-0 top-full z-50 mt-1.5 w-64 rounded-lg border border-brand-border bg-brand-surface-2 px-3 py-2 text-xs leading-relaxed text-brand-text shadow-pop"
        >
          {text}
        </span>
      )}
    </span>
  );
}
